import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check, AlertCircle, Users, Beaker } from "lucide-react";

function iconFor(type) {
  if (type === "lab_invite" || type === "lab_message") return Users;
  if (type === "reminder") return AlertCircle;
  return Beaker;
}

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const id = setInterval(tick, 60000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  const onOpen = async (n) => {
    try { await api.put(`/notifications/${n.id}/read`); } catch {}
    load();
    if (n.type === "lab_invite" || n.type === "lab_message") navigate(`/labs/${n.lab_id}`);
    else if (n.type === "reminder") navigate("/calendar");
  };

  const markAll = async () => {
    await api.put("/notifications/read-all");
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative h-9 w-9 rounded-md grid place-items-center hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] rounded-full bg-red-600 text-white text-[10px] font-mono font-bold px-1 grid place-items-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b border-zinc-200 dark:border-zinc-800">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Notifications</div>
          {data.unread > 0 && (
            <button onClick={markAll} className="text-xs font-mono text-blue-600 hover:underline" data-testid="mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="p-8 text-center text-sm font-mono text-zinc-500">all caught up</div>
          ) : (
            data.items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  onClick={() => onOpen(n)}
                  className={`w-full text-left p-3 flex gap-3 border-b border-zinc-100 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors ${
                    !n.read ? "bg-blue-50/40 dark:bg-blue-950/20" : ""
                  }`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${!n.read ? "text-blue-600" : "text-zinc-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-sm font-medium truncate">{n.title}</div>
                    {n.body && <div className="text-xs font-body text-zinc-500 line-clamp-2">{n.body}</div>}
                    <div className="text-xs font-mono text-zinc-400 mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-blue-600 mt-1 flex-shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
